import { blogData } from '@/features/blogs/data/blogs.content';

export interface BlogSearchResult {
  title: string;
  author?: string;
  speciality?: string;
  url: string;
}

const stripMarkdown = (value: string | undefined): string =>
  (value ?? '').replace(/^#+\s*/, '').trim();

interface BlogSearchEntry {
  result: BlogSearchResult;
  /** Lowercased text blob of every searchable field. */
  haystack: string;
  /** Lowercased title, used for relevance weighting. */
  title: string;
}

/**
 * Builds the searchable representation of one blog card.
 * Searches: title, description, speciality, topics, author, writtenBy,
 * table of contents, article body content, meta title and banner description.
 */
const toSearchEntry = (
  card: (typeof blogData.blogCards.cards)[number],
): BlogSearchEntry => {
  const title = stripMarkdown(card.title?.content);
  const author =
    stripMarkdown(card.author?.content) ||
    stripMarkdown(card.writtenBy?.content) ||
    undefined;

  const haystack = [
    title,
    stripMarkdown(card.description?.content),
    card.speciality ?? '',
    author ?? '',
    (card.topics?.topics ?? []).join(' '),
    (card.tableOfContents?.content ?? [])
      .map((tocItem) => tocItem.text)
      .join(' '),
    stripMarkdown(card.content?.content),
    stripMarkdown(card.metaTitle),
    stripMarkdown(
      typeof card.bannerDescription === 'string'
        ? card.bannerDescription
        : card.bannerDescription?.content,
    ),
  ]
    .join(' ')
    .toLowerCase();

  return {
    result: {
      title,
      author,
      speciality: card.speciality,
      url: card.slug ? `/blogs/${card.slug}` : '/blogs',
    },
    haystack,
    title: title.toLowerCase(),
  };
};

/** Lazily-built, cached search index over the static blog cards. */
let searchIndex: BlogSearchEntry[] | null = null;
const getSearchIndex = (): BlogSearchEntry[] => {
  if (!searchIndex) {
    searchIndex = blogData.blogCards.cards.map(toSearchEntry);
  }
  return searchIndex;
};

/**
 * Search health-blog content by title, description, speciality, topics,
 * author, table of contents and body content.
 *
 * Matching is intentionally "wide": the full query OR any individual word
 * of the query (>= 2 chars) counts as a match. Results are ranked by a
 * simple relevance score — number of matched query words, boosted by
 * matches in the title.
 */
export const searchBlogs = (query: string): BlogSearchResult[] => {
  const trimmed = query.toLowerCase().trim();
  if (trimmed.length < 2) return [];

  const tokens = Array.from(
    new Set(trimmed.split(/[^a-z0-9]+/).filter((t) => t.length >= 2)),
  );
  if (tokens.length === 0) return [];

  return getSearchIndex()
    .map((entry) => {
      const matchedTokens = tokens.filter((t) => entry.haystack.includes(t));
      if (matchedTokens.length === 0) return null;

      const titleHits = matchedTokens.filter((t) =>
        entry.title.includes(t),
      ).length;

      // Relevance: more matched words first; title matches boosted.
      const score = matchedTokens.length * 2 + titleHits * 3;

      return { entry, score };
    })
    .filter((hit): hit is { entry: BlogSearchEntry; score: number } =>
      Boolean(hit),
    )
    .sort((a, b) => b.score - a.score)
    .map((hit) => hit.entry.result);
};
